import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import { ResponsiveContainer } from 'recharts';

// import { chartTimePeriod } from '../../../config/enum'; // Unused
import currentTimeZone from '../../../config/timeZone';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import { TooltipIcon } from '../../../shared-components/tooltip/TooltipIcon';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import account from '../../../utils/api/account';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Energy report trend chart
 * This component displays the energy consumption trend over time.
 */
function PowerConsumedChart() {
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [activeRange, setActiveRange] = useState<string>('last24hours');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();
  const accountCustomId = id || searchParameters.get('accountCustomId');

  const monthMap: Record<string, string> = {
    Jan: 'jan',
    Feb: 'feb',
    Mar: 'mar',
    Apr: 'apr',
    May: 'may_month',
    Jun: 'jun',
    Jul: 'jul',
    Aug: 'aug',
    Sep: 'sep',
    Oct: 'oct',
    Nov: 'nov',
    Dec: 'dec',
  };
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        rtl: i18n.language === 'ar',
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        rtl: i18n.language === 'ar',
        textDirection: i18n.language === 'ar' ? 'rtl' : 'ltr',
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns {any} The tooltip title string.
           */
          title: () => `${t('powerConsumed')} (kWh)`,
          /**
           * Formats the tooltip label using locale string.
           * @param {any} context - The tooltip context object.
           * @returns {any} The formatted label string.
           */
          label: (context: any) => `${context.raw} kWh`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        reverse: i18n.language === 'ar',
        grid: { display: false },
        border: { display: false },
        title: {
          display: activeRange === 'last24hours',
          text: t('hours'),
          color: '#888',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /** Draws the vertical crosshair line using --success-dark theme color on hover */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a colored vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successDark = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successDark;
        context.stroke();
        context.restore();
      }
    },
  };

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param {any} range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setActiveRange(range);
    if (!accountCustomId) {
      return;
    }
    try {
      const response = await account.fetchPowerConsumedGraph(accountCustomId, {
        timePeriod: range,
        // reportType: 'powerConsumed',
      });

      if (response.status === 200) {
        const data = response.data.chartData;
        switch (range) {
          case 'last12months': {
            setBarGraphData(fillMissingMonthsGeneric(data, i18n.language));
            break;
          }
          case 'lastMonth': {
            setBarGraphData(fillMissingLastNDaysGeneric(data, 30, i18n.language));
            break;
          }
          case 'last7days': {
            setBarGraphData(fillMissingLastNDaysGeneric(data, 7, i18n.language));
            break;
          }
          case 'last24hours': {
            setBarGraphData(fillMissingLast24HrsGeneric(data, currentTimeZone, i18n.language));
            break;
          }
          default:
            setBarGraphData([]);
            break;
        }
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, [i18n.language, accountCustomId]);

  /**
   * Translate chart labels
   */
  const labels = barGraphData.map((d) => {
    const label = (d.label || '') as string;

    const parts = label.split(' ');
    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    return label;
  });
  const values = barGraphData.map((d) => ToFixedValue((Number(d.totalPowerConsumed || 0) / 1000),3));

  return (
    <div className="power-consumed-chart">
      <div className="power-consumed-container">
        <div className="report-header">
          <div className="report-title">
            <div className="title-wrapper">
              <p>{t('powerConsumed')}</p>
              <TooltipIcon text="Power consumed chart" infoColor="info" />
            </div>
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => {
                setDateRange({ startDate: start, endDate: end });
                // Trigger fetch with default range; adapt later to use selected dates
                fetchPowerConsumedReport('last24hours');
              }}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
            />
          </div>
        </div>

        <div className="power-consumed-container">
          <ResponsiveContainer width="100%" height={247}>
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: t('powerConsumed'),
                    data: values,
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: primaryColor,
                    pointBorderColor: primaryColor,
                  },
                ],
              }}
              options={chartOptions}
              plugins={[verticalHoverLinePlugin]}
            />
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default PowerConsumedChart;
